/* eslint-disable react/jsx-props-no-spreading */
import { FC, useMemo, useState } from 'react';
import {
  ActionIcon,
  Autocomplete,
  Box,
  Button,
  Center,
  Checkbox,
  Flex,
  Group,
  Loader,
  LoadingOverlay,
  MultiSelect,
  NumberInput,
  Select,
  Space,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
} from '@mantine/core';
import { closeAllModals, openModal } from '@mantine/modals';
import { i18n } from '@lingui/core';
import { IconCheck, IconChevronDown, IconPlus, IconSearch, IconTag, IconX } from '@tabler/icons';
import { useForm } from '@mantine/form';
import { useElementSize, useLocalStorage } from '@mantine/hooks';
import { CategoryDto, DiscountDto, DiscountType, PositionDto } from '../../../Service/DocumentT';
import { PositionS } from '../../../Service/PositionS';
import EmptyText from '../../../Atoms/EmptyText';
import { isDefined } from '../../../Service/ValidationS';
import { useSelection } from '../../../Service/hooks/useSelection';
import { replacePlaceholdersWithHtmlSettings } from '../../../Service/Placeholder/PlaceholderResover';
import { MessageText } from '../../../Atoms/MessagePlaceholderText';
import { BrowserS } from '../../../Service/BrowserS';
import { ToastS } from '../../../Service/ToastS';
import BookitupSegmentedControl from '../../../Atoms/BookitupSegmentedControl';
import AutocompleteResourceTypeItem from '../../../Organismns/Resources/table/AutocompleteResourceTypeItem';
import { RichInput } from '../../../Organismns/tiptap/TiptapEditorInput';
import DocumentBubbleMenu from '../../../Organismns/tiptap/toolbars/BubbleMenu';
import { DataS, LabelValuePair } from '../../../Service/DataS';
import { FormatS } from '../../../Service/FormatS';
import { TiptapS } from '../../../Service/TiptapS';
import BookitupBadge from '../../../Atoms/BookitupBadge';
import { useJwtClaims } from '../../../Service/Context/AuthContext';
import { LicenceS } from '../../../Service/LicenceS';
import { DocumentService } from '../../../Service/helper/DocumentService';
import FloatNumberInput from '../../../Atoms/inputs/FloatNumberInput';

interface AddProductModalProps {
  onFinish: (productsIds: number[]) => Promise<boolean>;
  products: PositionDto[];
}

// eslint-disable-next-line import/prefer-default-export
export const openAddProductModal = (props: AddProductModalProps) => {
  openModal({
    size: 'xl',
    trapFocus: false,
    title: (
      <Text weight="bolder" size="xl">
        Produktauswahl
      </Text>
    ),
    children: <AddProductModal {...props} />,
  });
};

interface ProductFilter {
  query: string;
  categories: string[];
}

const AddProductModal: FC<AddProductModalProps> = ({ onFinish, products }) => {
  const [loading, setLoading] = useState(false);
  const { licence } = useJwtClaims();
  const [filter, setFilter] = useState<ProductFilter>({
    query: '',
    categories: [],
  });
  const { collection, isSelected, toggle } = useSelection();
  const { ref, width } = useElementSize();

  const updateFilter = (patch: Partial<ProductFilter>) => setFilter((prev) => ({ ...prev, ...patch }));
  const allCategories = useMemo(() => {
    const categories = products.filter((p) => p.category !== null).map((p) => p.category);
    return Array.from(new Set(categories));
  }, [products]);
  const { query, categories } = filter;

  const filteredProducts = products.filter(
    (p) =>
      (!isDefined(query) ||
        p.label?.toLowerCase().includes(query.toLowerCase()) ||
        p.subtext?.toLowerCase().includes(query.toLowerCase())) &&
      (categories.length === 0 || (p.category !== null && categories.includes(p.category))),
  );
  const notFound = products.length > 0 && filteredProducts.length === 0;

  const onClick = async () => {
    setLoading(true);
    const ok = await onFinish(collection);
    setLoading(false);
    if (ok) {
      closeAllModals();
    }
  };

  const mapItemUI = (position: PositionDto) => {
    const { id, label, amount, price, priceUnit, unit, subtext, category } = position;
    const selected = isSelected(id);

    return (
      <Box
        p="sm"
        className="hooverable"
        sx={(theme) => ({
          borderBottom: `1px solid ${theme.colors.gray[2]}`,
          cursor: 'pointer',
        })}
        onClick={() => toggle(id)}
      >
        {category && (
          <BookitupBadge
            size="xs"
            my={5}
            ml={30}
            title={category}
            leftSection={<IconTag size={12} style={{ marginBottom: 2 }} />}
          />
        )}
        <Flex gap="md" wrap="wrap" align="center">
          {selected && (
            <ThemeIcon radius="xl" size="xs">
              <IconCheck size={12} />
            </ThemeIcon>
          )}
          {!selected && <Box sx={{ borderRadius: '50%', width: 16, height: 16, border: '1px solid gray' }} />}
          <Stack spacing={0} sx={{ flexGrow: 1 }}>
            <MessageText htmlValue={replacePlaceholdersWithHtmlSettings(i18n, label)} />
            {subtext && (
              <Text mt={5} size="xs" color="dimmed" className="overflow-ellipsis" sx={{ width: width - 90 }}>
                {BrowserS.getTextContent(subtext)}
              </Text>
            )}
            <Group position="apart" mt={5}>
              <Text italic size="xs">
                {amount || 1} {unit && unit} x {priceUnit} €
              </Text>
              <Text italic size="xs" weight="bolder">
                {price} €
              </Text>
            </Group>
          </Stack>
        </Flex>
      </Box>
    );
  };

  return (
    <Box ref={ref} sx={{ minHeight: 150, maxHeight: '70vh', overflow: 'scroll', scrollbarWidth: 'none' }}>
      <Box
        pos="fixed"
        sx={(theme) => ({
          width: '90%',
          zIndex: 100,
          backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : '#FFFFFF',
        })}
      >
        <Group position="center">
          <TextInput
            autoFocus
            value={query}
            onChange={(e) => updateFilter({ query: e.currentTarget.value })}
            variant="filled"
            placeholder="Produkt suchen"
            icon={<IconSearch size={16} />}
            rightSection={
              isDefined(query) && (
                <ActionIcon size="sm" onClick={() => updateFilter({ query: '' })}>
                  <IconX />
                </ActionIcon>
              )
            }
            sx={{ width: 300 }}
          />

          {LicenceS.hasSuitableLicence('PRO', licence) && allCategories.length > 0 && (
            <MultiSelect
              searchable
              value={categories}
              onChange={(val) => updateFilter({ categories: val })}
              variant="filled"
              icon={<IconTag size={20} />}
              placeholder="Gruppe suchen"
              data={(allCategories as []) ?? []}
              styles={{ rightSection: { pointerEvents: 'none' } }}
              rightSection={<IconChevronDown size={14} />}
              rightSectionWidth={25}
              sx={{ width: 300 }}
            />
          )}
        </Group>
      </Box>
      <Space h={window.innerWidth > 720 ? 50 : 100} />
      {loading && <LoadingOverlay visible={loading} overlayBlur={2} loaderProps={{ size: 'xl' }} />}
      {!loading && filteredProducts.map(mapItemUI)}
      {!loading && products.length === 0 && <EmptyText mt={40} />}
      {!loading && notFound && (
        <Text italic color="dimmed" align="center" mt={30}>
          Keine Produkte gefunden
        </Text>
      )}
      <Space h={100} />
      <Box
        pos="fixed"
        bottom={20}
        sx={(theme) => ({
          width: '91%',
          backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : '#FFFFFF',
        })}
      >
        <Center mt="xl">
          <Button onClick={onClick} leftIcon={<IconPlus size={16} />} disabled={collection.length === 0 || loading}>
            {i18n._('actions.add')}
          </Button>
        </Center>
        {collection.length > 0 && (
          <Text align="center" mt={5} size="xs" color="dimmed">{`${collection.length} Produkt${
            collection.length > 1 ? 'e' : ''
          } ausgewählt`}</Text>
        )}
      </Box>
    </Box>
  );
};

interface EditCategoryProps {
  product: PositionDto;
  categories: CategoryDto[];
  onConfirm: (newCategory: string | null) => void;
}

export const openEditPositionCategoryModal = (props: EditCategoryProps) => {
  openModal({
    title: (
      <Text weight="bolder" size="xl">
        Gruppe zuordnen
      </Text>
    ),
    children: <EditPositionCategoryModal {...props} />,
  });
};

const EditPositionCategoryModal: FC<EditCategoryProps> = ({ product, categories, onConfirm }) => {
  const { id: productId, categoryId } = product;
  const initialValue = categories.find((c) => c.id === categoryId)?.name;
  const [value, setValue] = useState<string | undefined>(initialValue);

  const onClick = async () => {
    const resJSON = await PositionS.editProduct(productId, { category: isDefined(value) ? value : null });
    if (resJSON) {
      onConfirm(resJSON.categoryId);
      closeAllModals();
    } else {
      ToastS.generalError();
    }
  };

  return (
    <>
      <Autocomplete
        icon={<IconTag size={20} />}
        label="Gruppe"
        value={value}
        data={categories.map((c) => c.name)}
        onChange={setValue}
        rightSection={
          isDefined(value) && (
            <ActionIcon size="xs" onClick={() => setValue(undefined)}>
              <IconX />
            </ActionIcon>
          )
        }
      />
      <Text mt={5} color="dimmed" size="xs">
        Die Gruppe wird automatisch erstellt, falls sie noch nicht vorhanden ist.
      </Text>
      <Center mt="xl">
        <Button onClick={onClick} disabled={value === initialValue}>
          {i18n._('actions.apply')}
        </Button>
      </Center>
    </>
  );
};

interface RenameCategoryProps {
  category: CategoryDto;
  existingCategories: string[];
  onConfirm: (newCategory: string) => void;
}

export const openRenameCategoryProps = (props: RenameCategoryProps) => {
  openModal({
    title: (
      <Text weight="bolder" size="xl">
        Gruppe bearbeiten
      </Text>
    ),
    children: <RenameCategoryModal {...props} />,
  });
};

const RenameCategoryModal: FC<RenameCategoryProps> = ({ category, existingCategories, onConfirm }) => {
  const { id, name } = category;
  const [value, setValue] = useState<string>(name);

  const onClick = async () => {
    if (value) {
      const res = await PositionS.editCategory(id, { name: value });
      if (res) {
        onConfirm(value);
        closeAllModals();
      } else {
        ToastS.generalError();
      }
    }
  };

  return (
    <>
      <Autocomplete
        icon={<IconTag size={20} />}
        label="Gruppe"
        value={value}
        data={existingCategories}
        onChange={setValue}
      />
      <Center mt="xl">
        <Button onClick={onClick} disabled={!isDefined(value) || category.name === value}>
          {i18n._('actions.apply')}
        </Button>
      </Center>
    </>
  );
};

interface CreateProductModalProps {
  onCreate: (position: PositionDto) => void;
  categories: string[];
  smallScaleBusiness: boolean;
  taxRates: LabelValuePair[];
  currencySymbol: string;
  position?: Partial<PositionDto>;
}

export const openCreateProductModal = (props: CreateProductModalProps) => {
  openModal({
    title: (
      <Text weight="bolder" size="xl">
        Produkt erstellen
      </Text>
    ),
    children: <CreateProductModal {...props} />,
  });
};

const CreateProductModal: FC<CreateProductModalProps> = ({
  onCreate,
  categories,
  smallScaleBusiness,
  position,
  currencySymbol,
  taxRates,
}) => {
  const form = useForm({
    initialValues: {
      label: '',
      subtext: '',
      priceUnit: 0,
      tax: smallScaleBusiness ? undefined : '19',
      amount: '1',
      unit: 'Stk.',
      category: undefined,
      discountValue: 0,
      discountType: 'PERCENT',
      ...position,
    },
  });

  const [productsTaxType] = useLocalStorage({
    key: 'productsTaxType',
    defaultValue: 'gross',
    getInitialValueInEffect: true,
  });

  const [loading, setLoading] = useState(false);
  const [usePlaceholderAmount, setPlaceholderAmount] = useState(false);

  const onSubmit = async () => {
    setLoading(true);
    const { tax, discountValue, discountType, priceUnit, ...rest } = form.values;
    const resJSON = await PositionS.addProduct({
      priceUnit:
        productsTaxType === 'gross' ? priceUnit : DocumentService.calculateGrossPrice(priceUnit, Number(tax ?? 0)),
      tax: Number(tax),
      discount: {
        type: discountType as DiscountType,
        value: discountValue,
      } as DiscountDto,
      ...rest,
    });
    if (resJSON) {
      onCreate(resJSON);
      closeAllModals();
    } else {
      ToastS.generalError();
    }
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Space h="md" />
      <RichInput
        label="Beschreibung"
        value={form.values.label}
        onUpdate={(val) => form.setValues({ label: val })}
        styles={TiptapS.TextInputStyles}
        toolbar={<DocumentBubbleMenu />}
      />
      <Space h="sm" />
      <RichInput
        label="Unterbeschreibung"
        value={form.values.subtext}
        onUpdate={(val) => form.setValues({ subtext: val })}
        styles={TiptapS.TextInputStyles}
        toolbar={<DocumentBubbleMenu />}
      />
      <Autocomplete
        mt="sm"
        icon={<IconTag size={20} />}
        label="Gruppe"
        data={categories}
        rightSection={
          isDefined(form.values.category) && (
            <ActionIcon size="xs" onClick={() => form.setValues({ category: undefined })}>
              <IconX />
            </ActionIcon>
          )
        }
        {...form.getInputProps('category')}
      />
      <Text mt={5} mb="sm" color="dimmed" size="xs">
        Die Gruppe wird automatisch erstellt, falls sie noch nicht vorhanden ist.
      </Text>
      {usePlaceholderAmount && (
        <Select
          label="Menge"
          data={DataS.amountPlaceholderOptions}
          itemComponent={AutocompleteResourceTypeItem}
          {...form.getInputProps('amount')}
          styles={{ rightSection: { pointerEvents: 'none' } }}
          rightSection={<IconChevronDown size={14} />}
          rightSectionWidth={25}
        />
      )}
      {!usePlaceholderAmount && (
        <NumberInput
          hideControls
          label="Menge"
          decimalSeparator=","
          value={isNaN(Number(form.values.amount)) ? undefined : Number(form.values.amount)}
          onBlur={(e) => form.setValues({ amount: e.currentTarget.value })}
          min={0}
        />
      )}
      <Checkbox
        mt="xs"
        size="xs"
        checked={usePlaceholderAmount}
        onChange={() => setPlaceholderAmount(!usePlaceholderAmount)}
        label="Platzhalterwert verwenden"
      />
      <Group position="apart" mt="sm">
        <Text>Einheit: </Text>
        <Autocomplete data={DataS.positionUnitSuggestions} {...form.getInputProps('unit')} sx={{ width: 100 }} />
      </Group>
      <Group position="apart" mt="sm">
        <Text>Preis: </Text>
        <FloatNumberInput
          hideControls
          decimalSeparator=","
          precision={Number.isInteger(form.values.priceUnit) ? 0 : 2}
          defaultValue={form.values.priceUnit}
          sx={{ width: 100 }}
          onChange={(val) => form.setValues({ priceUnit: val })}
          min={0}
        />
      </Group>
      {!smallScaleBusiness && (
        <Group position="apart" mt="sm">
          <Text>MwSt: </Text>
          <Select
            data={taxRates}
            {...form.getInputProps('tax')}
            styles={{ rightSection: { pointerEvents: 'none' } }}
            rightSection={<IconChevronDown size={14} />}
            rightSectionWidth={25}
            sx={{ width: 100 }}
          />
        </Group>
      )}
      <Group position="apart" mt="sm">
        <Text>Rabatt: </Text>
        <Group spacing={0}>
          <FloatNumberInput
            hideControls
            min={0}
            defaultValue={form.values.discountValue}
            decimalSeparator=","
            precision={Number.isInteger(form.values.discountValue) ? 0 : 2}
            sx={{ width: 60 }}
            onChange={(val) => form.setValues({ discountValue: val })}
          />
          <BookitupSegmentedControl
            data={DataS.getDiscountUnitOptions(currencySymbol)}
            {...form.getInputProps('discountType')}
          />
        </Group>
      </Group>
      <Center mt={40}>
        <Button type="submit" rightIcon={loading && <Loader size="xs" />} sx={{ minWidth: 230 }}>
          {i18n._('actions.save')}
        </Button>
      </Center>
    </form>
  );
};
